import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import CambiarContrasena from '@/components/CambiarContrasena';
import {
  ShieldCheck, Smartphone, KeyRound, QrCode, Copy, CheckCircle2,
  AlertTriangle, RefreshCw, PowerOff, Loader2, MessageSquare
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const card = "bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6";
const input = "w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm";

export default function SecuritySettingsPage() {
  const { token } = useAuth();
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);

  // TOTP enroll state
  const [totp, setTotp] = useState(null); // { secret, qr_data_url }
  const [totpCode, setTotpCode] = useState('');

  // SMS enroll state
  const [smsPhone, setSmsPhone] = useState('+1');
  const [smsSent, setSmsSent] = useState(null);
  const [smsCode, setSmsCode] = useState('');

  // Recovery codes
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable state
  const [disableCode, setDisableCode] = useState('');

  const refresh = useCallback(async () => {
    const r = await fetch(`${API}/api/mfa/status`, { headers });
    setStatus(await r.json());
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const call = async (path, body, method = 'POST') => {
    setBusy(true); setBanner(null);
    try {
      const r = await fetch(`${API}/api/mfa/${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Request failed');
      return data;
    } catch (e) { setBanner({ type: 'err', text: e.message || String(e) }); return null; }
    finally { setBusy(false); }
  };

  const startTotp = async () => {
    const r = await call('enroll/totp');
    if (r) setTotp(r);
  };

  const verifyTotp = async () => {
    const r = await call('enroll/totp/verify', { code: totpCode });
    if (r) {
      setBanner({ type: 'ok', text: 'Authenticator app enabled.' });
      if (r.recovery_codes) setRecoveryCodes(r.recovery_codes);
      setTotp(null); setTotpCode('');
      await refresh();
    }
  };

  const startSms = async () => {
    const r = await call('enroll/sms', { phone: smsPhone });
    // Si el SMS no sale, el servidor responde con error y `call` lo muestra. Ya no
    // existe el camino que mostraba el codigo en pantalla.
    if (r) {
      setSmsSent(r);
      setBanner({ type: 'ok', text: `Code sent to ····${r.phone_last4}` });
    }
  };

  const verifySms = async () => {
    const r = await call('enroll/sms/verify', { code: smsCode });
    if (r) {
      setBanner({ type: 'ok', text: 'SMS 2FA enabled.' });
      if (r.recovery_codes) setRecoveryCodes(r.recovery_codes);
      setSmsSent(null); setSmsCode('');
      await refresh();
    }
  };

  const regenerateCodes = async () => {
    const r = await call('recovery-codes/regenerate');
    if (r) { setRecoveryCodes(r.recovery_codes); setBanner({ type: 'ok', text: 'New recovery codes generated.' }); await refresh(); }
  };

  const disableMethod = async (method) => {
    if (!disableCode.trim()) { setBanner({ type: 'err', text: 'Enter a current verification code first.' }); return; }
    const r = await call(`disable/method/${method}`, { code: disableCode });
    if (r) { setBanner({ type: 'ok', text: `${method.toUpperCase()} disabled.` }); setDisableCode(''); await refresh(); }
  };

  const disableAll = async () => {
    if (!disableCode.trim()) { setBanner({ type: 'err', text: 'Enter a current verification code first.' }); return; }
    const r = await call('disable', { code: disableCode });
    if (r) { setBanner({ type: 'ok', text: 'Two-factor authentication fully disabled.' }); setDisableCode(''); setRecoveryCodes(null); await refresh(); }
  };

  if (!status) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="security-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Security · Two-Factor Authentication</h1>
            <p className="text-sm text-slate-500">Add a second layer to your account. Both authenticator apps and SMS are supported — you can enroll one or both.</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`} data-testid="mfa-state-badge">
          {status.enabled ? 'ENABLED' : 'DISABLED'}
        </div>
      </div>

      {status.must_enroll && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 text-amber-800 text-sm flex gap-2" data-testid="mfa-admin-required">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>Two-factor is required for platform admins. Please enroll below to continue using the admin dashboards.</span>
        </div>
      )}

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
          banner.type === 'ok'  ? 'bg-emerald-50 text-emerald-700' :
          banner.type === 'warn' ? 'bg-amber-50 text-amber-700' :
                                   'bg-rose-50 text-rose-700'
        }`} data-testid="mfa-banner">
          {banner.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{banner.text}</span>
        </div>
      )}

      {recoveryCodes && (
        <div className={card + " border-2 border-emerald-400"} data-testid="recovery-codes">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Recovery codes</h2>
              <p className="text-sm text-slate-500">Save these somewhere safe. Each can be used once to sign in if you lose your device. They will not be shown again.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))} data-testid="copy-recovery">
              <Copy className="w-3 h-3 mr-1" /> Copy all
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm">
            {recoveryCodes.map((c, i) => <div key={i} className="px-2 py-1.5 rounded bg-slate-100 dark:bg-slate-800 text-center">{c}</div>)}
          </div>
        </div>
      )}

      {/* TOTP */}
      <div className={card + " space-y-4"} data-testid="totp-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center"><QrCode className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <h3 className="font-semibold">Authenticator app (TOTP)</h3>
              <p className="text-sm text-slate-500">Google Authenticator · Authy · 1Password · any TOTP app</p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-semibold ${status.totp_enrolled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
            {status.totp_enrolled ? 'ENROLLED' : 'NOT ENROLLED'}
          </span>
        </div>
        {!status.totp_enrolled && !totp && (
          <Button onClick={startTotp} disabled={busy} data-testid="totp-start">Set up authenticator</Button>
        )}
        {totp && (
          <div className="space-y-3">
            <p className="text-sm">Scan this QR code in your authenticator app:</p>
            <img src={totp.qr_data_url} alt="TOTP QR code" className="w-48 h-48 border rounded-lg" data-testid="totp-qr" />
            <p className="text-xs text-slate-500">Or enter this secret manually: <code className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 font-mono text-xs" data-testid="totp-secret">{totp.secret}</code></p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-sm font-medium block mb-1.5">Enter the 6-digit code from the app</label>
                <input value={totpCode} onChange={e => setTotpCode(e.target.value)} placeholder="123456" className={input + " tracking-widest font-mono"} data-testid="totp-code-input" />
              </div>
              <Button onClick={verifyTotp} disabled={busy || totpCode.length < 6} data-testid="totp-verify">Verify & enable</Button>
            </div>
          </div>
        )}
      </div>

      {/* SMS */}
      <div className={card + " space-y-4"} data-testid="sms-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center"><Smartphone className="w-5 h-5 text-blue-600" /></div>
            <div>
              <h3 className="font-semibold">SMS text message</h3>
              <p className="text-sm text-slate-500">A 6-digit code sent to your phone via Twilio</p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-semibold ${status.sms_enrolled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
            {status.sms_enrolled ? `····${status.sms_phone_last4}` : 'NOT ENROLLED'}
          </span>
        </div>
        {!status.sms_enrolled && !smsSent && (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium block mb-1.5">Phone number (E.164)</label>
              <input value={smsPhone} onChange={e => setSmsPhone(e.target.value)} placeholder="+16195551212" className={input} data-testid="sms-phone-input" />
            </div>
            <Button onClick={startSms} disabled={busy} data-testid="sms-start"><MessageSquare className="w-4 h-4 mr-2" />Send code</Button>
          </div>
        )}
        {smsSent && (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium block mb-1.5">Enter the code we sent to ····{smsSent.phone_last4}</label>
              <input value={smsCode} onChange={e => setSmsCode(e.target.value)} placeholder="123456" className={input + " tracking-widest font-mono"} data-testid="sms-code-input" />
            </div>
            <Button onClick={verifySms} disabled={busy || smsCode.length < 6} data-testid="sms-verify">Verify</Button>
            <Button variant="outline" onClick={startSms} disabled={busy} data-testid="sms-resend">Resend</Button>
          </div>
        )}
      </div>

      {/* Recovery + Disable */}
      {status.enabled && (
        <div className={card + " space-y-4"} data-testid="manage-section">
          <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Manage two-factor</h3>
          <p className="text-sm text-slate-500">Recovery codes remaining: <b>{status.recovery_codes_remaining}</b> / 10</p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={regenerateCodes} disabled={busy} data-testid="regen-codes">
              <RefreshCw className="w-4 h-4 mr-2" /> Regenerate recovery codes
            </Button>
          </div>

          <div className="pt-3 border-t border-slate-200 dark:border-slate-800 space-y-3">
            <p className="text-sm">To disable a method, first enter a current TOTP or unused recovery code:</p>
            <input value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="Current code" className={input + " tracking-widest font-mono max-w-xs"} data-testid="disable-code-input" />
            <div className="flex gap-2 flex-wrap">
              {status.totp_enrolled && status.methods.length > 1 && (
                <Button variant="outline" onClick={() => disableMethod('totp')} disabled={busy} data-testid="disable-totp">Disable TOTP only</Button>
              )}
              {status.sms_enrolled && status.methods.length > 1 && (
                <Button variant="outline" onClick={() => disableMethod('sms')} disabled={busy} data-testid="disable-sms">Disable SMS only</Button>
              )}
              <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={disableAll} disabled={busy || status.admin_required} title={status.admin_required ? 'Platform admins cannot disable MFA' : ''} data-testid="disable-all">
                <PowerOff className="w-4 h-4 mr-2" /> Disable all two-factor
              </Button>
            </div>
            {status.admin_required && <p className="text-xs text-amber-600">Platform admins cannot fully disable MFA. Change your role first.</p>}
          </div>
        </div>
      )}

      <CambiarContrasena />
    </div>
  );
}
